import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { ProductImage } from "@eweadn-qiankun/shared-components";
import {
  getFixedMouseButtonIds,
  getMouseChangeKeyLayout,
  getMouseSideButtonDefaultLabels,
  type MouseChangeKeySpotId,
} from "@eweadn-qiankun/shared-utils";
import "./Mouse.scss";

interface MouseButton {
  id: MouseChangeKeySpotId;
  label: string;
  position: { x: number; y: number };
  labelPosition: { x: number; y: number };
  align?: "left" | "right";
}

export interface MouseVisualizerProps {
  deviceId: string;
  displayname: string;
  selectedButtonId: string | null;
  onButtonSelect: (buttonId: string | null) => void;
  buttonMappings: Record<string, string>;
  /** 改键预览：与默认映射不同的按键，始终展示选中态 */
  highlightedButtonIds?: string[];
  onResetToDefault?: () => void;
  hideReset?: boolean;
  readOnly?: boolean;
  /** 方案商（避免共享组件内使用子应用 `@/` 别名） */
  provider?: string;
}

/** 改键页鼠标可视化（无设备 hook，可供社区预览复用） */
export function MouseVisualizer({
  deviceId,
  displayname,
  selectedButtonId,
  onButtonSelect,
  buttonMappings,
  highlightedButtonIds = [],
  onResetToDefault,
  hideReset = false,
  readOnly = false,
  provider,
}: MouseVisualizerProps) {
  const { t } = useTranslation();
  const fixedButtonIds = useMemo(
    () => getFixedMouseButtonIds(deviceId),
    [deviceId]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const hotspotsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const labelsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [lines, setLines] = useState<{
    [key: string]: {
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      path?: string;
    };
  }>({});
  const [imageLoaded, setImageLoaded] = useState(false);

  const labelBySpotId: Record<MouseChangeKeySpotId, string> = useMemo(() => {
    const sideLabels = getMouseSideButtonDefaultLabels(deviceId, t);
    return {
      left: t("mouse.changeKey.leftButton"),
      middle: t("mouse.changeKey.middleButton"),
      right: t("mouse.changeKey.rightButton"),
      side1: sideLabels.side1,
      side2: sideLabels.side2,
      scrollUp: t("mouse.changeKey.scrollUp"),
      scrollDown: t("mouse.changeKey.scrollDown"),
      dpi: t("mouse.changeKey.dpiCycle"),
    };
  }, [deviceId, t]);

  const buttons: MouseButton[] = useMemo(() => {
    const spots = getMouseChangeKeyLayout(deviceId, { provider });
    return spots.map((spot) => ({
      ...spot,
      label: labelBySpotId[spot.id] ?? spot.id,
    }));
  }, [deviceId, labelBySpotId, provider]);

  const updateLines = useCallback(() => {
    if (!containerRef.current || !imageLoaded) return;

    requestAnimationFrame(() => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;
      const newLines: typeof lines = {};
      const TOP_BUTTON_IDS = new Set(["left", "middle", "right"]);
      const TOP_STEM_IDS = new Set(["left", "right"]);
      /** 右侧折线：滚轮两键水平段更长，DPI 更短（热点更靠右） */
      const RIGHT_STEM_LENGTH_BY_ID: Record<string, number> = {
        scrollUp: (92 / 264) * containerWidth,
        scrollDown: (92 / 264) * containerWidth,
        dpi: (42 / 264) * containerWidth,
      };
      const TOP_STEM_LENGTH = (32 / 474) * containerHeight;

      buttons.forEach((button) => {
        const hotspot = hotspotsRef.current[button.id];
        const label = labelsRef.current[button.id];
        const hotspotX = (button.position.x / 100) * containerWidth;
        const hotspotY = (button.position.y / 100) * containerHeight;
        const hotspotRadius = hotspot ? hotspot.offsetWidth / 2 : 14;
        const labelAnchorX = (button.labelPosition.x / 100) * containerWidth;
        const labelAnchorY = (button.labelPosition.y / 100) * containerHeight;
        const isTopButton = TOP_BUTTON_IDS.has(button.id);
        const hasTopStem = TOP_STEM_IDS.has(button.id);
        const rightStemLength = RIGHT_STEM_LENGTH_BY_ID[button.id];
        const hasRightStem = rightStemLength != null;
        const labelBottomOffset = label ? label.offsetHeight / 2 : 24;
        const labelHalfWidth = label ? label.offsetWidth / 2 : 48;
        const x2 = hasRightStem ? labelAnchorX - labelHalfWidth : labelAnchorX;
        const y2 = isTopButton
          ? labelAnchorY + labelBottomOffset
          : labelAnchorY;
        const elbowX = hasRightStem ? x2 - rightStemLength : x2;
        const elbowY = hasTopStem ? y2 + TOP_STEM_LENGTH : y2;
        const dx = elbowX - hotspotX;
        const dy = elbowY - hotspotY;
        const distance = Math.hypot(dx, dy) || 1;
        const x1 = hotspotX + (dx / distance) * hotspotRadius;
        const y1 = hotspotY + (dy / distance) * hotspotRadius;
        let path: string;
        if (hasTopStem) {
          path = `M ${x2} ${y2} L ${x2} ${elbowY} L ${x1} ${y1}`;
        } else if (hasRightStem) {
          path = `M ${x2} ${y2} L ${elbowX} ${y2} L ${x1} ${y1}`;
        } else {
          path = `M ${x2} ${y2} L ${x1} ${y1}`;
        }

        newLines[button.id] = { x1, y1, x2, y2, path };
      });

      setLines(newLines);
    });
  }, [buttons, imageLoaded]);

  useEffect(() => {
    if (imageLoaded) {
      const timer = setTimeout(updateLines, 50);
      return () => clearTimeout(timer);
    }
  }, [imageLoaded, updateLines]);

  useEffect(() => {
    if (imageLoaded) {
      updateLines();
    }
  }, [selectedButtonId, highlightedButtonIds, updateLines, imageLoaded]);

  useEffect(() => {
    if (!imageLoaded || !containerRef.current) return;

    const observer = new ResizeObserver(() => {
      updateLines();
    });
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [imageLoaded, updateLines]);

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly) return;
    if (e.target === e.currentTarget) {
      onButtonSelect(null);
    }
  };

  const isButtonSelected = (buttonId: string) =>
    highlightedButtonIds.includes(buttonId) || selectedButtonId === buttonId;

  return (
    <div className="mouse-container" onClick={handleContainerClick}>
      <div
        className="mouse-visualizer"
        ref={containerRef}
        onClick={handleContainerClick}
      >
        <svg className="connection-lines-container">
          {buttons.map((button) => {
            const line = lines[button.id];
            if (!line) return null;

            if (line.path) {
              return <path key={`line-${button.id}`} d={line.path} />;
            }

            return (
              <line
                key={`line-${button.id}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
              />
            );
          })}
        </svg>

        <ProductImage
          displayname={displayname}
          alt="Mouse"
          className="mouse-image"
          onLoad={() => setImageLoaded(true)}
        />

        {buttons.map((button) => {
          const isFixed = fixedButtonIds.includes(button.id);
          const selectButton = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (readOnly) return;
            onButtonSelect(button.id);
          };
          return (
            <div
              key={button.id}
              className={`mouse-button-group${isFixed ? " is-fixed" : ""}`}
            >
              <div
                ref={(el) => {
                  labelsRef.current[button.id] = el;
                }}
                className={`button-label ${isButtonSelected(button.id) ? "selected" : ""} ${button.align === "right" ? "align-right" : ""}${isFixed ? " disabled" : ""}`}
                style={{
                  left: `${button.labelPosition.x}%`,
                  top: `${button.labelPosition.y}%`,
                }}
                onClick={selectButton}
              >
                <div className="label-text">{button.label}</div>
                <div className="label-subtext">
                  {buttonMappings[button.id] || button.label}
                </div>
              </div>

              <div
                ref={(el) => {
                  hotspotsRef.current[button.id] = el;
                }}
                className={`mouse-button-hotspot ${isButtonSelected(button.id) ? "selected" : ""}${isFixed ? " disabled" : ""}`}
                style={{
                  left: `${button.position.x}%`,
                  top: `${button.position.y}%`,
                }}
                onClick={selectButton}
              />
            </div>
          );
        })}
      </div>

      {!hideReset && onResetToDefault ? (
        <div className="button-reset" onClick={onResetToDefault}>
          {t("mouse.changeKey.resetToDefault")}
        </div>
      ) : null}
    </div>
  );
}
